'use client';

import { ChecksIcon, Atom, Flask, Calculator, Percent } from '@phosphor-icons/react';
import TableHeader from '../../components/generic/TableHeader';
import StatCell from '../generic/StatCell';
import TableLoader from '../generic/TableLoader';
import NoData from '../generic/NoData';
import { QSTATUS } from '../../../../../apps/frontend/src/utils/strings';

const PerformanceSummary = ({
  data,
  loading,
  showPercentage,
  onTogglePercentage,
  onResetFilters, // Add this prop
}) => {
  const getIcon = (subjectTitle) => {
    switch (subjectTitle) {
      case 'Physics':
        return Atom;
      case 'Chemistry':
        return Flask;
      case 'Mathematics':
        return Calculator;
      default:
        return ChecksIcon;
    }
  };

  return (
    <div>
      <TableHeader headerTitle="Summary" headerSwitchCase={showPercentage} headerSwitchHandler={onTogglePercentage}>
        <div className="overflow-x-auto">
          <div className="min-w-[700px] border border-[var(--q3-stroke-light)] rounded-b-lg">
            {/* Header */}
            <div className="bg-[var(--q3-surface-dim)] border-b border-[var(--q3-stroke-light)]">
              <div className="font-medium grid grid-cols-[200px_1fr_1fr_1fr_1fr_1fr] divide-x divide-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]">
                <div className="p-4 flex items-center justify-start">Subject</div>
                <div className="p-4 text-center text-[var(--q3-orange-normal)]">
                  <div>Average</div>
                  <div>Score</div>
                </div>
                <div className="p-4 text-center text-[var(--q3-green-normal)]">
                  <div>Attempted</div>
                  <div>Correct</div>
                </div>
                <div className="p-4 text-center text-[var(--q3-danger-normal)]">
                  <div>Attempted</div>
                  <div>Wrong</div>
                </div>
                <div className="p-4 text-center text-[var(--q3-indigo-normal)]">
                  <div>Not</div>
                  <div>Attempted</div>
                </div>
                <div className="p-4 text-center text-[var(--q3-neutral-light)]">
                  <div>Not</div>
                  <div>Visited Qs</div>
                </div>
              </div>
            </div>

            {/* Data Rows */}
            <TableLoader loading={loading}>
              {data?.length === 0 && <NoData onResetAll={onResetFilters} />}

              {data?.map((subject, index) => {
                const iconUrl = subject?.subjectMeta?.icon;
                return (
                  <div key={index} className="border-b border-[var(--q3-stroke-light)] last:border-b-0">
                    <div className="grid grid-cols-[200px_1fr_1fr_1fr_1fr_1fr] divide-x divide-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]">
                      {/* Subject with icon */}
                      <div className="p-4 flex items-center gap-3">
                        {iconUrl && (
                          <img
                            src={iconUrl}
                            alt={subject?.subjectMeta?.title || 'Subject Icon'}
                            className="w-5 h-5 object-contain flex-shrink-0"
                          />
                        )}
                        <span className="font-medium">{subject?.subjectMeta?.title}</span>
                      </div>

                      {/* Average Score */}
                      <div className="text-center ">
                        <StatCell
                          color="var(--q3-orange-normal)"
                          top={subject?.totalMarkScored}
                          bottom={subject?.totalMarks}
                          showPercentage={showPercentage}
                          {...QSTATUS.AVERAGE}
                        />
                      </div>

                      {/* Attempted Correct */}
                      <div className="text-center text-green-600">
                        <StatCell
                          color="green"
                          top={subject?.correct}
                          bottom={subject?.totalQuestions}
                          showPercentage={showPercentage}
                          {...QSTATUS.CORRECT}
                        />
                      </div>

                      {/* Attempted Wrong */}
                      <div className="text-center text-red-600">
                        <StatCell
                          color="red"
                          top={subject?.incorrect}
                          bottom={subject?.totalQuestions}
                          showPercentage={showPercentage}
                          {...QSTATUS.INCORRECT}
                        />
                      </div>

                      {/* Not Attempted */}
                      <div className="text-center ">
                        <StatCell
                          color="var(--q3-indigo-normal)"
                          top={subject?.unanswered}
                          bottom={subject?.totalQuestions}
                          showPercentage={showPercentage}
                          {...QSTATUS.NOT_ATTEMPTED}
                        />
                      </div>

                      {/* Not Visited */}
                      <div className="text-center ">
                        <StatCell
                          color="var(--q3-indigo-normal)"
                          top={subject?.notVisited}
                          bottom={subject?.totalQuestions}
                          showPercentage={showPercentage}
                          {...QSTATUS.NOT_VISITED}
                          themedText={true}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </TableLoader>
          </div>
        </div>
      </TableHeader>
    </div>
  );
};

export default PerformanceSummary;
